import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

// Image Zoom Modal with Navigation - Rendered via Portal to be truly fullscreen
const ImageZoom = ({ images, currentIndex, isOpen, onClose, onIndexChange }) => {
  const [localIndex, setLocalIndex] = useState(currentIndex);
  
  // Sync with external index
  useEffect(() => {
    setLocalIndex(currentIndex);
  }, [currentIndex]);

  const goNext = useCallback((e) => {
    e?.stopPropagation();
    const newIndex = (localIndex + 1) % images.length;
    setLocalIndex(newIndex);
    onIndexChange?.(newIndex);
  }, [localIndex, images.length, onIndexChange]);

  const goPrev = useCallback((e) => {
    e?.stopPropagation();
    const newIndex = (localIndex - 1 + images.length) % images.length;
    setLocalIndex(newIndex);
    onIndexChange?.(newIndex);
  }, [localIndex, images.length, onIndexChange]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        goNext();
      } else if (e.key === 'ArrowLeft') {
        goPrev();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, goNext, goPrev]);

  if (!isOpen || !images || images.length === 0) return null;
  
  const currentImage = images[localIndex];
  
  // Use portal to render at document body level
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 bg-black/95 flex flex-col items-center justify-center"
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      {/* Main image container */}
      <div className="relative flex items-center justify-center w-full h-full px-16">
        {/* Previous button */}
        {images.length > 1 && (
          <button
            onClick={goPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 hover:bg-white/40 transition-colors z-10"
          >
            <ChevronLeft className="w-8 h-8 text-white" />
          </button>
        )}

        {/* Image with animation */}
        <AnimatePresence mode="wait">
          <motion.img
            key={localIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            src={currentImage.image}
            alt={`Slide ${localIndex + 1}`}
            className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg shadow-2xl cursor-default"
            onClick={(e) => e.stopPropagation()}
          />
        </AnimatePresence>

        {/* Next button */}
        {images.length > 1 && (
          <button
            onClick={goNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 hover:bg-white/40 transition-colors z-10"
          >
            <ChevronRight className="w-8 h-8 text-white" />
          </button>
        )}
      </div>

      {/* Caption */}
      <AnimatePresence mode="wait">
        <motion.div
          key={localIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 max-w-2xl px-6 py-3 bg-black/70 rounded-xl text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-white/90 text-sm leading-relaxed">{currentImage.caption}</p>
        </motion.div>
      </AnimatePresence>

      {/* Slide counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/60 rounded-full text-white text-sm font-medium">
        {localIndex + 1} / {images.length}
      </div>
      
      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 p-3 rounded-full bg-white/20 hover:bg-white/40 transition-colors"
      >
        <X className="w-8 h-8 text-white" />
      </button>
      
      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setLocalIndex(index);
                onIndexChange?.(index);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                index === localIndex 
                  ? 'bg-white w-6' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      )}
      
      {/* Instructions */}
      <p className="absolute bottom-1 left-1/2 -translate-x-1/2 text-white/50 text-xs">
        ← → untuk navigasi • ESC untuk menutup
      </p>
    </motion.div>,
    document.body
  );
};

// Guide Carousel Component
const GuideCarousel = ({ guides, guideTitle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  const nextSlide = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % guides.length);
  };

  const prevSlide = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + guides.length) % guides.length);
  };

  const handleImageClick = () => {
    setIsZoomed(true);
  };

  const closeZoom = () => {
    setIsZoomed(false);
  };

  const handleIndexChange = (newIndex) => {
    setCurrentIndex(newIndex);
  };

  return (
    <>
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-5 border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          {guideTitle}
        </h3>
        
        {/* Carousel Container */}
        <div className="relative">
          {/* Image Container */}
          <div 
            className="relative overflow-hidden rounded-xl bg-gray-100 aspect-video group cursor-zoom-in"
            onClick={handleImageClick}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={currentIndex}
                src={guides[currentIndex].image}
                alt={`Panduan ${currentIndex + 1}`}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="w-full h-full object-contain pointer-events-none"
              />
            </AnimatePresence>
            
            {/* Zoom hint overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-3 py-2 rounded-full flex items-center gap-2 shadow-lg">
                <ZoomIn className="w-4 h-4 text-gray-700" />
                <span className="text-sm text-gray-700 font-medium">Klik untuk zoom</span>
              </div>
            </div>
            
            {/* Navigation Arrows */}
            {guides.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all hover:scale-110 z-10"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all hover:scale-110 z-10"
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </>
            )}
            
            {/* Slide Counter */}
            <div className="absolute bottom-2 right-2 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-medium z-10 pointer-events-none">
              {currentIndex + 1} / {guides.length}
            </div>
          </div>
          
          {/* Caption */}
          <AnimatePresence mode="wait">
            <motion.p
              key={currentIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="mt-4 text-gray-600 text-sm leading-relaxed"
            >
              {guides[currentIndex].caption}
            </motion.p>
          </AnimatePresence>
          
          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-4">
            {guides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-red-500 w-6' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Image Zoom Modal with Navigation */}
      <ImageZoom 
        images={guides}
        currentIndex={currentIndex}
        isOpen={isZoomed} 
        onClose={closeZoom}
        onIndexChange={handleIndexChange}
      />
    </>
  );
};

const ServiceModal = ({ service, isOpen, onClose }) => {
  if (!service) return null;

  // Determine action link - use service-specific link or default to email
  const actionLink = service.actionLink || "mailto:dirpusdadig@ppi.id";
  const isExternalLink = service.actionLink && !service.actionLink.startsWith('mailto:');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-4 md:inset-10 lg:inset-20 z-50 flex items-center justify-center pointer-events-none"
          >
            <div 
              className="relative w-full max-w-2xl max-h-[85vh] pointer-events-auto overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderRadius: '24px',
                border: '1px solid rgba(255,255,255,0.5)',
                boxShadow: `
                  0 0 0 1px rgba(255,255,255,0.3) inset,
                  0 25px 50px -12px rgba(0,0,0,0.25),
                  0 0 100px rgba(220,38,38,0.1)
                `
              }}
            >
              {/* Glass shine effect */}
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                  borderRadius: '24px'
                }}
              />

              {/* Liquid blob decorations */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-red-200/40 to-orange-200/40 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-blue-200/40 to-cyan-200/40 rounded-full blur-3xl" />

              {/* Close button - Higher z-index */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-lg cursor-pointer"
                style={{ zIndex: 100 }}
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>

              {/* Content - Scrollable */}
              <div className="relative z-10 overflow-y-auto max-h-[85vh] p-6 md:p-8 pt-14">
                {/* Header */}
                <div className="flex items-start gap-4 mb-6 pr-8">
                  {service.logo ? (
                    <img 
                      src={service.logo} 
                      alt={service.title}
                      className="w-16 h-16 rounded-2xl object-contain shadow-lg"
                    />
                  ) : (
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-lg`}>
                      <span className="text-3xl">
                        {service.icon === 'Mail' && '📧'}
                        {service.icon === 'Video' && '🎥'}
                        {service.icon === 'Globe' && '🌐'}
                        {service.icon === 'MessageSquare' && '💬'}
                      </span>
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                      {service.detailTitle}
                    </h2>
                    <p className="text-gray-500 mt-1">{service.title}</p>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-6" />

                {/* Guide Carousel - Show at TOP if guides exist */}
                {service.guides && service.guides.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mb-6"
                  >
                    <GuideCarousel 
                      guides={service.guides} 
                      guideTitle={service.guideTitle || "Panduan"} 
                    />
                  </motion.div>
                )}

                {/* Detail sections */}
                <div className="space-y-6">
                  {service.details.map((detail, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: (index + 1) * 0.1 }}
                      className="bg-white/50 rounded-2xl p-5 border border-white/60 shadow-sm"
                    >
                      <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500" />
                        {detail.subtitle}
                      </h3>
                      <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                        {detail.content}
                      </p>
                    </motion.div>
                  ))}
                </div>

                {/* CTA Button */}
                <div className="mt-8 pt-6 border-t border-gray-200/50">
                  <a 
                    href={actionLink}
                    target={isExternalLink ? "_blank" : undefined}
                    rel={isExternalLink ? "noopener noreferrer" : undefined}
                    className="block w-full py-4 rounded-2xl bg-red-600 text-white font-semibold text-lg hover:bg-red-700 transition-colors shadow-lg shadow-red-600/25 text-center"
                  >
                    Ajukan Layanan Ini
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ServiceModal;
